import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { lighten, rgba } from 'color2k';
import { Flex } from '$components/layouts';
import { SvgIcon } from '$components/elements/svg-icon';
import { Text } from '$components/elements/text';
import { Button } from '$components/elements/button';
import { theme } from '$theme';
import { IExpresseDeliveryResponse } from '$models';
import { formatString, mq } from '$lib/helpers';
import { useTranslation } from '$hooks';

type Props = {
    data?: IExpresseDeliveryResponse | null;
    price?: string;
    isLoading?: boolean;
    onClick: () => void;
};

export const ExpressDelivery = ({ data, isLoading, onClick }: Props) => {
    const { translate } = useTranslation();
    const expressDeliveryOption = useMemo(() => data?.storeDeliveryShippingTypes?.[0], [data]);

    if (expressDeliveryOption) {
        return (
            <Container allowed={true}>
                <SvgIcon svg="bikefast" size={24} color={theme.colors.textDefault} />
                <Flex gap="small" column flex>
                    <Text variant="smallHeader" fontWeight="Bold">
                        {translate('productPage.delivery.sameDayTitle')}
                    </Text>
                    <ExpressDeliveryButton width="expansive" loading={isLoading} onClick={onClick}>
                        {formatString(
                            translate('productPage.delivery.sameDayButtonText'),
                            expressDeliveryOption.deliveryTime?.toLowerCase() || ''
                        )}
                    </ExpressDeliveryButton>
                    {data?.storeInformation?.storeDeliveryCutoffTime && (
                        <Flex alignItems="center" gap="extrasmall">
                            <SvgIcon size={24} svg="info" color={theme.colors.secondaryBlueDarkBase} />
                            <Text color={theme.colors.secondaryBlueDarkBase}>
                                {formatString(
                                    translate('productPage.delivery.sameDayCutoffTime'),
                                    data.storeInformation.storeDeliveryCutoffTime,
                                    expressDeliveryOption.deliveryTime?.toLowerCase() || ''
                                )}
                            </Text>
                        </Flex>
                    )}
                </Flex>
            </Container>
        );
    }

    return (
        <Container>
            <SvgIcon svg="bike" size={24} color={theme.colors.textSubtle} />
            <Flex gap="extrasmall" column>
                <Text variant="smallHeader" fontWeight="Bold">
                    {translate('productPage.delivery.sameDayTitle')}
                </Text>
                <Text color={theme.colors.textSubtle}>{translate('productPage.delivery.sameDayNotAvailable')}</Text>
            </Flex>
        </Container>
    );
};

const Container = styled.li<{ allowed?: boolean }>(({ allowed, theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: rgba(0, 0, 0, 0.01),
    padding: `${theme.space[7]} ${theme.space[5]} ${theme.space[8]} ${theme.space[5]}`,
    gap: theme.space[5],
    ...(allowed && {
        order: -1,
    }),

    [mq('frame')]: {
        padding: `${theme.space[9]} ${theme.space[10]} ${theme.space[8]} ${theme.space[7]}`,
    },
}));

const ExpressDeliveryButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.colors.secondaryBlueDarkBase,

    ':hover': {
        backgroundColor: lighten(theme.colors.secondaryBlueDarkBase, 0.1),
    },
}));
